import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import "../styles/admin.css";

const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || "http://127.0.0.1:8000").replace(/\/+$/, "");

function Dashboard() {
  const navigate = useNavigate();
  const [message, setMessage] = useState("");
  const [user, setUser] = useState("");
  const [role, setRole] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [uploadingBanner, setUploadingBanner] = useState(false);
  const [uploadingInitiative, setUploadingInitiative] = useState(false);
  const [banners, setBanners] = useState([]);
  const [statistics, setStatistics] = useState([]);
  const [initiatives, setInitiatives] = useState([]);
  const [visionMission, setVisionMission] = useState(null);
  const [bannerForm, setBannerForm] = useState({
    id: null,
    title: "",
    description: "",
    image_url: "",
    order: 0,
    status: "active",
  });
  const [statForm, setStatForm] = useState({
    id: null,
    label: "",
    value: "",
    order: 0,
    status: "active",
  });
  const [initiativeForm, setInitiativeForm] = useState({
    id: null,
    title: "",
    description: "",
    image_url: "",
    order: 0,
    status: "active",
  });
  const [visionForm, setVisionForm] = useState({
    vision_title: "",
    vision_description: "",
    mission_title: "",
    mission_description: "",
  });

  const token = useMemo(() => localStorage.getItem("token"), []);

  useEffect(() => {
    if (!token) {
      navigate("/");
      return;
    }

    const loadDashboard = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/dashboard/`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          localStorage.clear();
          navigate("/");
          return;
        }

        const data = await response.json();
        setMessage(data.message || "Welcome to dashboard");
        setUser(data.user || localStorage.getItem("email") || "");
        if (data.role) {
          localStorage.setItem("role", data.role);
          setRole(data.role);
          if (!["admin", "staff"].includes(data.role)) {
            navigate("/");
            return;
          }
        }

        const headers = { Authorization: `Bearer ${token}` };
        const [bannerRes, statRes, initiativeRes, visionRes] = await Promise.all([
          fetch(`${API_BASE_URL}/cms/admin/banners/`, { headers }),
          fetch(`${API_BASE_URL}/cms/admin/statistics/`, { headers }),
          fetch(`${API_BASE_URL}/cms/admin/initiatives/`, { headers }),
          fetch(`${API_BASE_URL}/cms/admin/vision-mission/`, { headers }),
        ]);

        if (bannerRes.ok) {
          const bannerData = await bannerRes.json();
          setBanners(bannerData.items || []);
        }
        if (statRes.ok) {
          const statData = await statRes.json();
          setStatistics(statData.items || []);
        }
        if (initiativeRes.ok) {
          const initiativeData = await initiativeRes.json();
          setInitiatives(initiativeData.items || []);
        }
        if (visionRes.ok) {
          const visionData = await visionRes.json();
          if (visionData.item) {
            setVisionMission(visionData.item);
            setVisionForm({
              vision_title: visionData.item.vision_title,
              vision_description: visionData.item.vision_description,
              mission_title: visionData.item.mission_title,
              mission_description: visionData.item.mission_description,
            });
          }
        }
      } catch (error) {
        console.error(error);
        setError("Failed to load dashboard data");
      }
    };

    loadDashboard().finally(() => setLoading(false));
  }, [navigate, token]);

  const handleLogout = () => {
    localStorage.clear();
    navigate("/");
  };

  const resetBannerForm = () =>
    setBannerForm({ id: null, title: "", description: "", image_url: "", order: 0, status: "active" });
  const resetStatForm = () =>
    setStatForm({ id: null, label: "", value: "", order: 0, status: "active" });
  const resetInitiativeForm = () =>
    setInitiativeForm({
      id: null,
      title: "",
      description: "",
      image_url: "",
      order: 0,
      status: "active",
    });

  const showSuccess = (message) => {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(""), 3000);
  };

  const saveBanner = async () => {
    if (!token) return;
    if (uploadingBanner) {
      setError("Please wait for the banner image upload to finish.");
      return;
    }
    if (!bannerForm.image_url) {
      setError("Upload an image or paste an image URL before saving.");
      return;
    }
    if (!window.confirm("Save changes to this banner?")) {
      return;
    }
    setError("");
    const payload = {
      title: bannerForm.title,
      description: bannerForm.description,
      image_url: bannerForm.image_url,
      order: Number(bannerForm.order) || 0,
      status: bannerForm.status,
    };

    const response = await fetch(
      bannerForm.id
        ? `${API_BASE_URL}/cms/admin/banners/${bannerForm.id}/`
        : `${API_BASE_URL}/cms/admin/banners/`,
      {
        method: bannerForm.id ? "PATCH" : "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      }
    );

    if (!response.ok) {
      setError("Failed to save banner");
      return;
    }

    const data = await response.json();
    setBanners((prev) => {
      if (bannerForm.id) {
        return prev.map((item) => (item.id === bannerForm.id ? data : item));
      }
      return [...prev, data].sort((a, b) => a.order - b.order || a.id - b.id);
    });
    resetBannerForm();
    showSuccess("Changes saved live.");
  };

  const deleteBanner = async (id) => {
    if (!token) return;
    if (!window.confirm("Delete this banner?")) {
      return;
    }
    const response = await fetch(`${API_BASE_URL}/cms/admin/banners/${id}/`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    });
    if (response.ok) {
      setBanners((prev) => prev.filter((item) => item.id !== id));
      showSuccess("Changes saved live.");
    }
  };

  const saveStatistic = async () => {
    if (!token) return;
    if (!window.confirm("Save changes to this statistic?")) {
      return;
    }
    setError("");
    const payload = {
      label: statForm.label,
      value: statForm.value,
      order: Number(statForm.order) || 0,
      status: statForm.status,
    };

    const response = await fetch(
      statForm.id
        ? `${API_BASE_URL}/cms/admin/statistics/${statForm.id}/`
        : `${API_BASE_URL}/cms/admin/statistics/`,
      {
        method: statForm.id ? "PATCH" : "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      }
    );

    if (!response.ok) {
      setError("Failed to save statistic");
      return;
    }

    const data = await response.json();
    setStatistics((prev) => {
      if (statForm.id) {
        return prev.map((item) => (item.id === statForm.id ? data : item));
      }
      return [...prev, data].sort((a, b) => a.order - b.order || a.id - b.id);
    });
    resetStatForm();
    showSuccess("Changes saved live.");
  };

  const deleteStatistic = async (id) => {
    if (!token) return;
    if (!window.confirm("Soft delete this statistic?")) {
      return;
    }
    const response = await fetch(`${API_BASE_URL}/cms/admin/statistics/${id}/`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    });
    if (response.ok) {
      setStatistics((prev) => prev.map((item) => (item.id === id ? { ...item, is_deleted: true } : item)));
      showSuccess("Changes saved live.");
    }
  };

  const saveInitiative = async () => {
    if (!token) return;
    if (uploadingInitiative) {
      setError("Please wait for the initiative image upload to finish.");
      return;
    }
    if (!initiativeForm.image_url) {
      setError("Upload an image or paste an image URL before saving.");
      return;
    }
    if (!window.confirm("Save changes to this initiative?")) {
      return;
    }
    setError("");
    const payload = {
      title: initiativeForm.title,
      description: initiativeForm.description,
      image_url: initiativeForm.image_url,
      order: Number(initiativeForm.order) || 0,
      status: initiativeForm.status,
    };

    const response = await fetch(
      initiativeForm.id
        ? `${API_BASE_URL}/cms/admin/initiatives/${initiativeForm.id}/`
        : `${API_BASE_URL}/cms/admin/initiatives/`,
      {
        method: initiativeForm.id ? "PATCH" : "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      }
    );

    if (!response.ok) {
      setError("Failed to save initiative");
      return;
    }

    const data = await response.json();
    setInitiatives((prev) => {
      if (initiativeForm.id) {
        return prev.map((item) => (item.id === initiativeForm.id ? data : item));
      }
      return [...prev, data].sort((a, b) => a.order - b.order || a.id - b.id);
    });
    resetInitiativeForm();
    showSuccess("Changes saved live.");
  };

  const deleteInitiative = async (id) => {
    if (!token) return;
    if (!window.confirm("Delete this initiative?")) {
      return;
    }
    const response = await fetch(`${API_BASE_URL}/cms/admin/initiatives/${id}/`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    });
    if (response.ok) {
      setInitiatives((prev) => prev.filter((item) => item.id !== id));
      showSuccess("Changes saved live.");
    }
  };

  const saveVisionMission = async () => {
    if (!token) return;
    if (!window.confirm("Save mission and vision updates?")) {
      return;
    }
    setError("");
    const response = await fetch(`${API_BASE_URL}/cms/admin/vision-mission/`, {
      method: visionMission ? "PATCH" : "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(visionForm),
    });

    if (!response.ok) {
      setError("Failed to save mission and vision");
      return;
    }

    const data = await response.json();
    setVisionMission(data);
    showSuccess("Changes saved live.");
  };

  const uploadImage = async (file, onSuccess, setUploading) => {
    if (!token || !file) return;
    setError("");
    setUploading(true);

    const formData = new FormData();
    formData.append("file", file);

    const response = await fetch(`${API_BASE_URL}/cms/admin/upload-image/`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    });

    if (!response.ok) {
      let message = "Image upload failed. Please try again.";
      try {
        const errorData = await response.json();
        if (errorData?.message) {
          message = errorData.message;
        }
      } catch (parseError) {
        console.error(parseError);
      }
      setError(message);
      setUploading(false);
      return;
    }

    const data = await response.json();
    if (data.url) {
      onSuccess(data.url);
      showSuccess("Image uploaded successfully.");
    } else {
      setError("Image upload failed. No URL returned.");
    }
    setUploading(false);
  };

  if (loading) {
    return <p style={{ textAlign: "center", marginTop: "40px" }}>Loading dashboard...</p>;
  }

  return (
    <div className="admin-shell">
      <div className="admin-container">
        <div className="admin-header">
          <div>
            <h1 className="admin-title">CMS Dashboard</h1>
            <p className="admin-subtitle">{message || "Manage homepage content"}</p>
            <p className="admin-subtitle">Signed in as {user || ""}</p>
            {role && (
              <span className={`admin-pill ${role === "staff" ? "" : "warning"}`}>
                Role: {role}
              </span>
            )}
          </div>
          <div className="admin-actions">
            <button className="admin-btn neutral" type="button" onClick={() => navigate("/")}
              >Back to Site</button>
            {role === "admin" && (
              <button className="admin-btn secondary" type="button" onClick={() => navigate("/admin/users")}
                >Manage Users</button>
            )}
            <button className="admin-btn danger" type="button" onClick={handleLogout}
              >Logout</button>
          </div>
        </div>

        {error && <p style={{ color: "#b91c1c" }}>{error}</p>}
        {successMessage && <div className="admin-alert">{successMessage}</div>}

        <div className="admin-grid">
          <div className="admin-card">
            <h3>Vision & Mission</h3>
            <div className="admin-form">
              <label className="admin-field">
                Vision Title
                <input
                  value={visionForm.vision_title}
                  onChange={(event) =>
                    setVisionForm((prev) => ({ ...prev, vision_title: event.target.value }))
                  }
                />
              </label>
              <label className="admin-field">
                Vision Description
                <textarea
                  rows="3"
                  value={visionForm.vision_description}
                  onChange={(event) =>
                    setVisionForm((prev) => ({ ...prev, vision_description: event.target.value }))
                  }
                />
              </label>
              <label className="admin-field">
                Mission Title
                <input
                  value={visionForm.mission_title}
                  onChange={(event) =>
                    setVisionForm((prev) => ({ ...prev, mission_title: event.target.value }))
                  }
                />
              </label>
              <label className="admin-field">
                Mission Description
                <textarea
                  rows="3"
                  value={visionForm.mission_description}
                  onChange={(event) =>
                    setVisionForm((prev) => ({ ...prev, mission_description: event.target.value }))
                  }
                />
              </label>
              <button className="admin-btn" type="button" onClick={saveVisionMission}
                >Save Vision & Mission</button>
            </div>
          </div>

          <div className="admin-card">
            <h3>Hero Banners</h3>
            <div className="admin-split">
              <div className="admin-form">
                <label className="admin-field">
                  Title
                  <input
                    value={bannerForm.title}
                    onChange={(event) => setBannerForm((prev) => ({ ...prev, title: event.target.value }))}
                  />
                </label>
                <label className="admin-field">
                  Description
                  <textarea
                    rows="3"
                    value={bannerForm.description}
                    onChange={(event) =>
                      setBannerForm((prev) => ({ ...prev, description: event.target.value }))
                    }
                  />
                </label>
                <label className="admin-field">
                  Image URL
                  <input
                    value={bannerForm.image_url}
                    onChange={(event) => setBannerForm((prev) => ({ ...prev, image_url: event.target.value }))}
                  />
                </label>
                <label className="admin-field">
                  Upload Image
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(event) =>
                      uploadImage(
                        event.target.files?.[0],
                        (url) => setBannerForm((prev) => ({ ...prev, image_url: url })),
                        setUploadingBanner
                      )
                    }
                  />
                </label>
                {uploadingBanner && <div className="admin-pill">Uploading image...</div>}
                <label className="admin-field">
                  Order
                  <input
                    type="number"
                    value={bannerForm.order}
                    onChange={(event) => setBannerForm((prev) => ({ ...prev, order: event.target.value }))}
                  />
                </label>
                <label className="admin-field">
                  Status
                  <select
                    value={bannerForm.status}
                    onChange={(event) => setBannerForm((prev) => ({ ...prev, status: event.target.value }))}
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </label>
                <div className="admin-row-actions">
                  <button className="admin-btn" type="button" onClick={saveBanner} disabled={uploadingBanner}>
                    {uploadingBanner
                      ? "Uploading..."
                      : bannerForm.id
                      ? "Update Banner"
                      : "Add Banner"}
                  </button>
                  <button className="admin-btn neutral" type="button" onClick={resetBannerForm}>
                    Clear
                  </button>
                </div>
              </div>
              <div>
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Title</th>
                      <th>Order</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {banners.map((banner) => (
                      <tr key={banner.id}>
                        <td>{banner.title}</td>
                        <td>{banner.order}</td>
                        <td>{banner.status}</td>
                        <td>
                          <div className="admin-row-actions">
                            <button
                              className="admin-btn secondary"
                              type="button"
                              onClick={() => setBannerForm({
                                id: banner.id,
                                title: banner.title,
                                description: banner.description || "",
                                image_url: banner.image_url,
                                order: banner.order,
                                status: banner.status,
                              })}
                            >
                              Edit
                            </button>
                            <button
                              className="admin-btn danger"
                              type="button"
                              onClick={() => deleteBanner(banner.id)}
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div className="admin-card">
            <h3>Impact Statistics</h3>
            <div className="admin-split">
              <div className="admin-form">
                <label className="admin-field">
                  Label
                  <input
                    value={statForm.label}
                    onChange={(event) => setStatForm((prev) => ({ ...prev, label: event.target.value }))}
                  />
                </label>
                <label className="admin-field">
                  Value
                  <input
                    value={statForm.value}
                    onChange={(event) => setStatForm((prev) => ({ ...prev, value: event.target.value }))}
                  />
                </label>
                <label className="admin-field">
                  Order
                  <input
                    type="number"
                    value={statForm.order}
                    onChange={(event) => setStatForm((prev) => ({ ...prev, order: event.target.value }))}
                  />
                </label>
                <label className="admin-field">
                  Status
                  <select
                    value={statForm.status}
                    onChange={(event) => setStatForm((prev) => ({ ...prev, status: event.target.value }))}
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </label>
                <div className="admin-row-actions">
                  <button className="admin-btn" type="button" onClick={saveStatistic}>
                    {statForm.id ? "Update Statistic" : "Add Statistic"}
                  </button>
                  <button className="admin-btn neutral" type="button" onClick={resetStatForm}>
                    Clear
                  </button>
                </div>
              </div>
              <div>
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Label</th>
                      <th>Value</th>
                      <th>Status</th>
                      <th>Deleted</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {statistics.map((stat) => (
                      <tr key={stat.id}>
                        <td>{stat.label}</td>
                        <td>{stat.value}</td>
                        <td>{stat.status}</td>
                        <td>{stat.is_deleted ? "Yes" : "No"}</td>
                        <td>
                          <div className="admin-row-actions">
                            <button
                              className="admin-btn secondary"
                              type="button"
                              onClick={() => setStatForm({
                                id: stat.id,
                                label: stat.label,
                                value: stat.value,
                                order: stat.order,
                                status: stat.status,
                              })}
                            >
                              Edit
                            </button>
                            <button
                              className="admin-btn danger"
                              type="button"
                              onClick={() => deleteStatistic(stat.id)}
                            >
                              Soft Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div className="admin-card">
            <h3>Initiatives</h3>
            <div className="admin-split">
              <div className="admin-form">
                <label className="admin-field">
                  Title
                  <input
                    value={initiativeForm.title}
                    onChange={(event) =>
                      setInitiativeForm((prev) => ({ ...prev, title: event.target.value }))
                    }
                  />
                </label>
                <label className="admin-field">
                  Description
                  <textarea
                    rows="3"
                    value={initiativeForm.description}
                    onChange={(event) =>
                      setInitiativeForm((prev) => ({ ...prev, description: event.target.value }))
                    }
                  />
                </label>
                <label className="admin-field">
                  Image URL
                  <input
                    value={initiativeForm.image_url}
                    onChange={(event) =>
                      setInitiativeForm((prev) => ({ ...prev, image_url: event.target.value }))
                    }
                  />
                </label>
                <label className="admin-field">
                  Upload Image
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(event) =>
                      uploadImage(
                        event.target.files?.[0],
                        (url) => setInitiativeForm((prev) => ({ ...prev, image_url: url })),
                        setUploadingInitiative
                      )
                    }
                  />
                </label>
                {uploadingInitiative && <div className="admin-pill">Uploading image...</div>}
                <label className="admin-field">
                  Order
                  <input
                    type="number"
                    value={initiativeForm.order}
                    onChange={(event) =>
                      setInitiativeForm((prev) => ({ ...prev, order: event.target.value }))
                    }
                  />
                </label>
                <label className="admin-field">
                  Status
                  <select
                    value={initiativeForm.status}
                    onChange={(event) =>
                      setInitiativeForm((prev) => ({ ...prev, status: event.target.value }))
                    }
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </label>
                <div className="admin-row-actions">
                  <button
                    className="admin-btn"
                    type="button"
                    onClick={saveInitiative}
                    disabled={uploadingInitiative}
                  >
                    {uploadingInitiative
                      ? "Uploading..."
                      : initiativeForm.id
                      ? "Update Initiative"
                      : "Add Initiative"}
                  </button>
                  <button className="admin-btn neutral" type="button" onClick={resetInitiativeForm}>
                    Clear
                  </button>
                </div>
              </div>
              <div>
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Title</th>
                      <th>Order</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {initiatives.map((initiative) => (
                      <tr key={initiative.id}>
                        <td>{initiative.title}</td>
                        <td>{initiative.order}</td>
                        <td>{initiative.status}</td>
                        <td>
                          <div className="admin-row-actions">
                            <button
                              className="admin-btn secondary"
                              type="button"
                              onClick={() => setInitiativeForm({
                                id: initiative.id,
                                title: initiative.title,
                                description: initiative.description,
                                image_url: initiative.image_url,
                                order: initiative.order,
                                status: initiative.status,
                              })}
                            >
                              Edit
                            </button>
                            <button
                              className="admin-btn danger"
                              type="button"
                              onClick={() => deleteInitiative(initiative.id)}
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;